import { lazy, useEffect, useState } from "react";
import {
  Redirect,
  RedirectProps,
  Route,
  RouteProps,
  Switch,
  useHistory,
} from "react-router-dom";

import RequireAuthentication from "@/containers/RequireAuthentication";
import RequireTour from "@/containers/RequireTour";
import RequireTourAndProspect from "@/containers/RequireTourAndProspect";
import { useAppState } from "@/context/AppState";
import { useApi } from "@/hooks/api";
import { MustBe18 } from "@/routes/MustBe18";
import { NearbyCommunities } from "@/routes/NearbyCommunities";
import { latestTosVersion } from "@/utils/tos";
import { isV2FrontendEnabled, setV2FrontendEnabled } from "@/utils/v2";

const Map = lazy(() => import("@/routes/Map"));
const Agreements = lazy(() => import("@/routes/Agreements"));
const ContactInformation = lazy(() => import("@/routes/ContactInformation"));
const VerifyMobileNumber = lazy(() => import("@/routes/VerifyMobileNumber"));
const SMSVerificationConsent = lazy(
  () => import("@/routes/SMSVerificationConsent")
);
const ExpiringLinkRedirect = lazy(
  () => import("@/routes/ExpiringLinkRedirect")
);
const EndOfTourPage = lazy(() => import("@/routes/EndOfTourPage"));
const InteractiveMap = lazy(() => import("@/routes/InteractiveMap"));
const Tour = lazy(() => import("@/routes/Tour"));
const Units = lazy(() => import("@/routes/Units"));
const CreateForUnits = lazy(() => import("@/routes/CreateForUnits"));
const Schedule = lazy(() => import("@/routes/Schedule"));
const ContactDetailsForm = lazy(() => import("@/routes/ContactDetailsForm"));
const PetPolicy = lazy(() => import("@/routes/PetPolicy"));
const Amenity = lazy(() => import("@/routes/Amenity"));
const TourCancelled = lazy(() => import("@/routes/TourCancelled"));
const VerifyID = lazy(() => import("@/routes/VerifyID"));
const DocumentSelection = lazy(
  () => import("@/routes/Verify/DocumentSelection")
);
const SupportedCountries = lazy(
  () => import("@/routes/Verify/SupportedCountries")
);
const DriversLicense = lazy(() => import("@/routes/Verify/DriversLicence"));
const Passport = lazy(() => import("@/routes/Verify/Passport"));
const Selfie = lazy(() => import("@/routes/Verify/Selfie"));
const ManualVerification = lazy(() => import("@/routes/ManualVerification"));
const InvalidLink = lazy(() => import("@/routes/InvalidLink"));
const NotFound = lazy(() => import("@/NotFound"));

// v2 routes are defined here
const UnitsV2 = lazy(() => import("@/routes/v2/Units"));
const ScheduleV2 = lazy(() => import("@/routes/v2/Schedule"));
const SMSVerificationConsentV2 = lazy(
  () => import("@/routes/v2/SMSVerificationConsent")
);
const ContactInfoV2 = lazy(() => import("@/routes/v2/ContactInformation"));
const DocumentSelectionV2 = lazy(() => import("@/routes/v2/DocumentSelection"));
const UploadDriversLicense = lazy(
  () => import("@/routes/v2/UploadDriversLicense")
);
const UploadPassport = lazy(() => import("@/routes/v2/UploadPassport"));
const UploadManualVerification = lazy(
  () => import("@/routes/v2/UploadManualVerification")
);
const SelfieV2 = lazy(() => import("@/routes/v2/Selfie"));

const VerifyMobileNumberV2 = lazy(
  () => import("@/routes/v2/VerifyMobileNumber")
);
const PreTourChecklist = lazy(() => import("@/routes/v2/PreTourChecklist"));
const TourV2 = lazy(() => import("@/routes/v2/Tour"));

type AppRoute =
  | (RouteProps & { type: "route" })
  | (RedirectProps & { type: "redirect" });

const isV2Frontend = isV2FrontendEnabled();

const routeMap: AppRoute[] = [
  {
    type: "route",
    path: "/expiring-link/:apiTokenJti",
    exact: true,
    component: ExpiringLinkRedirect,
  },
  {
    type: "redirect",
    from: "/property/:groupTourConfigUuid",
    to: "/property/:groupTourConfigUuid/units",
    exact: true,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/units",
    exact: true,
    component: isV2Frontend ? UnitsV2 : Units,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/schedule",
    exact: true,
    component: CreateForUnits,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/pet-policy",
    exact: true,
    component: PetPolicy,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/prospect-waitlist/sms-verification-consent",
    exact: true,
    component: isV2Frontend ? SMSVerificationConsentV2 : SMSVerificationConsent,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/prospect-waitlist/verify-mobile",
    exact: true,
    component: isV2Frontend
      ? VerifyMobileNumberV2
      : RequireTourAndProspect(VerifyMobileNumber),
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/prospect-waitlist/agreements",
    exact: true,
    component: Agreements,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/prospect-waitlist/contact-info",
    exact: true,
    component: isV2Frontend ? ContactInfoV2 : ContactInformation,
  },
  // The other Units route (/property/:uuid/units) is whenever
  // we haven't created a tour yet. This one, on the other hand,
  // is for editing the units on an already-created tour, so we
  // need to pre-load the tour before rendering it.
  {
    type: "route",
    path: "/tour/:tourToken/units",
    exact: true,
    component: isV2Frontend ? RequireTour(UnitsV2) : RequireTour(Units),
  },
  {
    type: "route",
    path: "/tour/:tourToken/schedule",
    exact: true,
    component: isV2Frontend ? RequireTour(ScheduleV2) : RequireTour(Schedule),
  },
  {
    type: "route",
    path: "/tour/:tourToken/sms-verification-consent",
    exact: true,
    component: isV2Frontend ? SMSVerificationConsentV2 : SMSVerificationConsent,
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify-mobile",
    exact: true,
    component: isV2Frontend ? VerifyMobileNumberV2 : VerifyMobileNumber,
  },
  {
    type: "route",
    path: "/tour/:tourToken/agreements",
    exact: true,
    component: RequireTour(Agreements),
  },
  {
    type: "route",
    path: "/tour/:tourToken/contact-info",
    exact: true,
    component: RequireTour(isV2Frontend ? ContactInfoV2 : ContactInformation),
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify",
    exact: true,
    component: ContactDetailsForm,
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify/document-selection",
    exact: true,
    component: isV2Frontend
      ? RequireTour(DocumentSelectionV2)
      : RequireTour(DocumentSelection),
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify/drivers-license",
    exact: true,
    component: isV2Frontend
      ? RequireTour(UploadDriversLicense)
      : RequireTour(DriversLicense),
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify/passport",
    exact: true,
    component: isV2Frontend
      ? RequireTour(UploadPassport)
      : RequireTour(Passport),
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify/selfie",
    exact: true,
    component: isV2Frontend ? RequireTour(SelfieV2) : RequireTour(Selfie),
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify/supported-countries",
    exact: true,
    component: RequireTour(SupportedCountries),
  },
  {
    type: "route",
    path: "/tour/:tourToken/verify/id",
    exact: true,
    component: isV2Frontend
      ? RequireAuthentication(RequireTourAndProspect(UploadManualVerification))
      : RequireAuthentication(RequireTourAndProspect(VerifyID)),
  },
  {
    type: "route",
    path: "/tour/:tourToken/manual-verification-required",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(ManualVerification)
    ),
  },
  {
    type: "route",
    path: "/tour/:tourToken/nearby-communities",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(NearbyCommunities, {
        allowFinalizedTours: true,
      })
    ),
  },
  {
    type: "route",
    path: "/tour/:tourToken/checklist",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(PreTourChecklist, {
        allowFinalizedTours: true,
      })
    ),
  },
  {
    // IN TOUR route
    type: "route",
    path: "/tour/:tourToken",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(isV2Frontend ? TourV2 : Tour, {
        allowFinalizedTours: true,
      })
    ),
  },
  {
    type: "route",
    path: "/tour/:tourToken/amenity/:id",
    exact: true,
    component: Amenity,
  },
  {
    type: "route",
    path: "/tour/:tourToken/community-map",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(Map, {
        allowFinalizedTours: true,
      })
    ),
  },
  {
    type: "route",
    path: "/tour/:tourToken/end-of-tour",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(EndOfTourPage, {
        allowFinalizedTours: true,
      })
    ),
  },
  {
    type: "route",
    path: "/tour/:tourToken/interactive-map",
    exact: true,
    component: RequireAuthentication(
      RequireTourAndProspect(InteractiveMap, {
        allowFinalizedTours: true,
      })
    ),
  },
  {
    type: "route",
    path: "/tour/:tourToken/cancelled",
    exact: true,
    component: TourCancelled,
  },
  {
    type: "route",
    path: "/tour/:tourToken/must-be-18",
    exact: true,
    component: MustBe18,
  },
  {
    type: "route",
    path: "/invalid-link",
    exact: true,
    component: InvalidLink,
  },
  {
    type: "route",
    path: "/property/:groupTourConfigUuid/invalid-link",
    exact: true,
    component: InvalidLink,
  },
  {
    type: "route",
    path: "/terms_of_service",
    exact: true,
    component: () => {
      window.location.href = `/assets/${latestTosVersion}`;
      return null;
    },
  },
];

const Routes = () => {
  const history = useHistory();
  const [{ config }, _setAppState] = useAppState();
  const groupId = config?.groupId;
  // Add a flag to track if we've already handled the response
  const [hasHandledFlag, setHasHandledFlag] = useState(false);
  const currentV2State = isV2FrontendEnabled();

  // Checks the feature flag for tours v2 UI from CMW
  // Only make the API call if config.uuid exists
  const { response } = useApi({
    url: config?.uuid
      ? `/config/${config.uuid}/feature-flag/tours_v2_disabled`
      : null,
    method: "GET",
    trigger: [],
    forceDispatchEffect: () => !!config?.uuid,
  });

  // THIS IS REALLY JANK, we need to redo this later
  type Gate = {
    enabled: boolean;
    for:
      | "global"
      | {
          entity: string;
          entity_id: number;
        };
  };

  type FeatureFlag = {
    gates: Gate[];
    name: string;
  };

  useEffect(() => {
    if (response?.data && !hasHandledFlag && groupId) {
      // Find the tours_v2_disabled flag
      const featureFlag = response.data.find(
        (flag: FeatureFlag) => flag.name === "tours_v2_disabled"
      );

      if (featureFlag) {
        // Find the gate that matches our group
        const groupGate = featureFlag.gates.find(
          (gate: Gate) =>
            typeof gate.for !== "string" &&
            gate.for.entity === "group" &&
            gate.for.entity_id === Number(groupId)
        );

        // Get the flag value, preferring group-specific setting over global
        const featureFlagValue =
          groupGate?.enabled ??
          featureFlag.gates.find((gate: Gate) => gate.for === "global")
            ?.enabled ??
          false;

        const newValue = !featureFlagValue; // Negate since flag is "tours_v2_disabled"
        const shouldUpdate = newValue !== currentV2State;

        if (shouldUpdate) {
          setV2FrontendEnabled(`${newValue}`);
          history.replace({ search: "" });
          location.reload();
        }
      }

      setHasHandledFlag(true);
    }
  }, [response, history, hasHandledFlag, currentV2State, groupId]);

  return (
    <Switch>
      {routeMap.map((route, index) => {
        if (route.type === "route") {
          return <Route key={`route-${index}`} {...route} />;
        } else {
          return <Redirect key={`redirect-${index}`} {...route} />;
        }
      })}
      <Route component={NotFound} />
    </Switch>
  );
};

export default Routes;
